import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { Pagination } from '../../../shared/pagination/pagination';
import { PaginationOptions } from '../../../shared/pagination/pagination-options';
import { SurveysFSEService } from '../../../shared/services/surveys-fse.service';
import { SortDirection, SortEvent } from '../../../shared/sortable/sort-event';
import { SortableDirective } from '../../../shared/sortable/sortable.directive';
import { ToastService } from '../../../shared/toast/toast.service';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { BeneficiaryBaseComponent } from '../../beneficiary-base.component';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { DisplayFSE } from './displayFSE';
import { FseDetailsComponent } from './fse-details/fse-details.component';
import { SurveyContext, labelsBySurveyContext } from '../../../shared/models/enums/survey-context-id.enum';

@Component({
  selector: 'app-fse',
  templateUrl: './fse.component.html',
  standalone: false,
})
export class FSEComponent extends BeneficiaryBaseComponent implements OnInit, OnDestroy {
  private static readonly resetSurveysFSE: Pagination<DisplayFSE> = new Pagination([], 0, 0, 1, new PaginationOptions(1, 25));
  public user: UserDto | undefined = undefined;
  public canAnswerSurvey: boolean = false;
  public SurveyContext = SurveyContext;
  public labelsBySurveyContext = labelsBySurveyContext;

  constructor(
    private readonly fseService: SurveysFSEService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
    private readonly authService: AuthService,
    private readonly beneficiariesService: BeneficiariesService,
  ) {
    super();
  }

  @ViewChildren(SortableDirective) headers: QueryList<SortableDirective>;
  public surveysFSE: Pagination<DisplayFSE> = FSEComponent.resetSurveysFSE;

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();
    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));

    this.canAnswerSurvey = !!this.beneficiary.orientation?.beginDate;
    await this.updateSurveysFSE(this.surveysFSE.options);
    this.fseService.surveySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateSurveysFSE(this.surveysFSE.options);
    });
  }

  public async onSort(event: SortEvent): Promise<void> {
    // resetting other headers
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    await this.updateSurveysFSE(this.surveysFSE.options.withSort(column, direction == 'desc'));
  }

  public async changePage(pageNb?: number): Promise<void> {
    if (pageNb) {
      await this.updateSurveysFSE(this.surveysFSE.options.withPage(pageNb));
    }
  }

  public async changeLimit(event: any): Promise<void> {
    if (event && event.target && event.target.value) {
      const limit: number = event.target.value;
      await this.updateSurveysFSE(this.surveysFSE.options.withLimit(limit));
    }
  }

  private async updateSurveysFSE(pagination: PaginationOptions): Promise<void> {
    this.surveysFSE = await this.fseService.findByBeneficiaryId(this.beneficiary.id, pagination);
  }

  public async openCreateModal(): Promise<void> {
    if (this.user && this.user.groups && this.user.groups.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+'].includes(group))) {
      this.openModal();
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
    }
  }

  public async openConsultModal(surveyFseId?: number): Promise<void> {
    if (
      this.user &&
      this.user.groups &&
      this.user.groups.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+', 'TERRITORY'].includes(group))
    ) {
      try {
        await this.fseService.findBySurveyId(surveyFseId);
        this.openModal(surveyFseId);
      } catch (error) {
        if (error.status === 437) {
          this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
        }
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
    }
  }

  private async openModal(surveyFseId?: number): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(FseDetailsComponent, {
      windowClass: 'rightModal',
      size: 'lg',
      backdrop: 'static',
    });
    const modalComponent: FseDetailsComponent = modal.componentInstance as FseDetailsComponent;
    await modalComponent.onInit(surveyFseId);
  }
}
